import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText,
  Search,
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  Archive,
  RefreshCw,
  User,
  X,
  ShieldCheck,
  BookOpen,
  UserCog,
  Clock,
} from 'lucide-react';

const ACTION_CONFIG = {
  created: { label: 'Aangemaakt', color: 'bg-green-50 text-green-700 border-green-200', icon: Plus },
  updated: { label: 'Bijgewerkt', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Pencil },
  deleted: { label: 'Verwijderd', color: 'bg-red-50 text-red-700 border-red-200', icon: Trash2 },
  completed: { label: 'Afgerond', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: CheckCircle2 },
  archived: { label: 'Gearchiveerd', color: 'bg-slate-50 text-slate-700 border-slate-200', icon: Archive },
  status_changed: { label: 'Status gewijzigd', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: RefreshCw },
  deactivated: { label: 'Gedeactiveerd', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: UserCog },
  email_sent: { label: 'E-mail verstuurd', color: 'bg-cyan-50 text-cyan-700 border-cyan-200', icon: Clock },
};

const ENTITY_CONFIG = {
  vault_entry: { label: 'Kennisbank', icon: BookOpen, color: 'text-blue-600' },
  compliance_item: { label: 'Compliance', icon: ShieldCheck, color: 'text-emerald-600' },
  user: { label: 'Gebruiker', icon: User, color: 'text-purple-600' },
  notification: { label: 'Notificatie', icon: Clock, color: 'text-cyan-600' },
};

function formatTimestamp(ts) {
  if (!ts) return '';
  try {
    const date = new Date(ts);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function formatFieldName(field) {
  const map = {
    title: 'Titel', content: 'Inhoud', location_id: 'Locatie', bv_id: 'Bedrijfsdomein',
    category: 'Categorie', subcategory: 'Subcategorie', tags: 'Tags', entry_status: 'Status',
    type: 'Type', description: 'Omschrijving', due_date: 'Vervaldatum',
    assigned_to: 'Toegewezen aan', link_to_vault_entry: 'Gekoppeld item',
    name: 'Naam', role: 'Rol', status: 'Status', notes: 'Notities',
    recurrence: 'Herhaling', reminder_days: 'Herinneringen',
    notification_preferences: 'Notificatie-instellingen',
    recipient: 'Ontvanger', subject: 'Onderwerp',
  };
  return map[field] || field;
}

function truncate(str, len = 60) {
  if (!str) return '';
  const s = String(str);
  return s.length > len ? s.substring(0, len) + '...' : s;
}

export default function Logboek() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const api = authAxios();
      const params = new URLSearchParams();
      if (filterEntity !== 'all') params.append('entity_type', filterEntity);
      if (filterAction !== 'all') params.append('action', filterAction);
      if (filterUser !== 'all') params.append('user_id', filterUser);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (dateFrom) params.append('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.append('date_to', new Date(dateTo + 'T23:59:59').toISOString());
      params.append('page', page.toString());
      params.append('limit', '30');

      const [logsRes, usersRes] = await Promise.all([
        api.get(`/audit-logs?${params}`),
        api.get('/users/list'),
      ]);

      setLogs(logsRes.data.logs || []);
      setTotal(logsRes.data.total || 0);
      setPages(logsRes.data.pages || 1);
      setUsers(usersRes.data || []);
    } catch (e) {
      console.error('Failed to load audit logs:', e);
      toast.error('Fout bij laden logboek');
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterEntity, filterAction, filterUser, searchQuery, dateFrom, dateTo, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const clearFilters = () => {
    setFilterEntity('all');
    setFilterAction('all');
    setFilterUser('all');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasFilters = filterEntity !== 'all' || filterAction !== 'all' || filterUser !== 'all' || searchQuery || dateFrom || dateTo;

  const handleEntityClick = (log) => {
    if (log.entity_type === 'vault_entry' && log.action !== 'deleted') {
      navigate(`/kennisbank/${log.entity_id}`);
    } else if (log.entity_type === 'compliance_item' && log.action !== 'deleted') {
      navigate('/compliance');
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="space-y-4 max-w-[1100px]" data-testid="logboek-page">
        <div className="bg-white rounded-xl border border-slate-200/80 p-4">
          <Skeleton className="h-9 w-full mb-3" />
          <Skeleton className="h-9 w-2/3" />
        </div>
        <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-4">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
              <div className="flex-1">
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-[1100px]" data-testid="logboek-page">
      {/* Stats strip */}
      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl border border-slate-200/80 px-4 py-2 flex items-center gap-2 card-hover">
          <FileText className="w-4 h-4 text-[#2E75B6]" />
          <span className="text-sm font-medium text-slate-700 tabular-nums" data-testid="logboek-total-count">
            {total} {total === 1 ? 'logboek item' : 'logboek items'}
          </span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200/80 p-3 sm:p-4" data-testid="logboek-filter-bar">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Onderdeel</label>
              <Select value={filterEntity} onValueChange={(v) => { setFilterEntity(v); setPage(1); }}>
                <SelectTrigger className="h-10 sm:h-9 text-sm" data-testid="logboek-filter-entity">
                  <SelectValue placeholder="Alle onderdelen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle onderdelen</SelectItem>
                  <SelectItem value="vault_entry">Kennisbank</SelectItem>
                  <SelectItem value="compliance_item">Compliance</SelectItem>
                  <SelectItem value="user">Gebruiker</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Actie</label>
              <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(1); }}>
                <SelectTrigger className="h-10 sm:h-9 text-sm" data-testid="logboek-filter-action">
                  <SelectValue placeholder="Alle acties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle acties</SelectItem>
                  <SelectItem value="created">Aangemaakt</SelectItem>
                  <SelectItem value="updated">Bijgewerkt</SelectItem>
                  <SelectItem value="deleted">Verwijderd</SelectItem>
                  <SelectItem value="completed">Afgerond</SelectItem>
                  <SelectItem value="status_changed">Status gewijzigd</SelectItem>
                  <SelectItem value="deactivated">Gedeactiveerd</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Gebruiker</label>
              <Select value={filterUser} onValueChange={(v) => { setFilterUser(v); setPage(1); }}>
                <SelectTrigger className="h-10 sm:h-9 text-sm" data-testid="logboek-filter-user">
                  <SelectValue placeholder="Alle gebruikers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle gebruikers</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Zoeken</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  placeholder="Zoek op titel of naam..."
                  className="h-10 sm:h-9 text-sm pl-8"
                  data-testid="logboek-search-input"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Van <span className="normal-case text-slate-400">(dag-maand-jaar)</span></label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="h-9 text-sm w-36"
                  data-testid="logboek-date-from"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Tot <span className="normal-case text-slate-400">(dag-maand-jaar)</span></label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="h-9 text-sm w-36"
                  data-testid="logboek-date-to"
                />
              </div>
            </div>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-9 text-xs text-slate-500 self-end"
                data-testid="logboek-clear-filters"
              >
                <X className="w-3 h-3 mr-1" /> Wis filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Log feed */}
      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="logboek-feed">
        {logs.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500" data-testid="logboek-empty-state">
              {hasFilters ? 'Geen logboek items gevonden voor de gekozen filters.' : 'Nog geen wijzigingen gelogd.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((log) => (
              <LogEntry key={log.log_id} log={log} onEntityClick={handleEntityClick} />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200/80 px-4 py-3" data-testid="logboek-pagination">
          <span className="text-xs text-slate-500 tabular-nums">
            Pagina {page} van {pages} ({total} items)
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              data-testid="logboek-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page >= pages}
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              data-testid="logboek-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LogEntry({ log, onEntityClick }) {
  const actionCfg = ACTION_CONFIG[log.action] || ACTION_CONFIG.updated;
  const entityCfg = ENTITY_CONFIG[log.entity_type] || ENTITY_CONFIG.vault_entry;
  const ActionIcon = actionCfg.icon;
  const EntityIcon = entityCfg.icon;
  const changes = log.changes || {};
  const changeEntries = Object.entries(changes).filter(([k]) => k !== 'source' && k !== 'notes');
  const hasChanges = changeEntries.length > 0 && log.action !== 'completed';

  return (
    <div className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50/50 transition-colors duration-150" data-testid="logboek-entry">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${actionCfg.color}`}>
        <ActionIcon className="w-3.5 h-3.5" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-2">
          <div className="text-sm text-slate-900">
            <span className="font-medium">{log.user_name || log.user_id}</span>
            {' '}
            <span className="text-slate-500">{actionCfg.label.toLowerCase()}</span>
            {' '}
            <span className="inline-flex items-center gap-1">
              <EntityIcon className={`w-3 h-3 inline ${entityCfg.color}`} />
              <span className="text-[10px] uppercase tracking-wider text-slate-400">{entityCfg.label}</span>
            </span>
            {' '}
            {log.entity_type !== 'user' && log.action !== 'deleted' ? (
              <button
                onClick={() => onEntityClick(log)}
                className="font-medium text-[#2E75B6] hover:underline"
                data-testid="logboek-entry-entity-link"
              >
                {truncate(log.entity_title, 50)}
              </button>
            ) : (
              <span className="font-medium text-slate-700">{truncate(log.entity_title, 50)}</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge variant="outline" className={`text-[10px] ${actionCfg.color}`} data-testid="logboek-entry-action-badge">
              {actionCfg.label}
            </Badge>
            <span className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap flex items-center gap-1" data-testid="logboek-entry-timestamp">
              <Clock className="w-3 h-3" />
              {formatTimestamp(log.timestamp)}
            </span>
          </div>
        </div>

        {hasChanges && (
          <div className="mt-2 space-y-1 pl-0" data-testid="logboek-entry-changes">
            {changeEntries.map(([field, diff]) => {
              const formatVal = (v) => {
                if (v === null || v === undefined) return '\u2014';
                if (typeof v === 'object') return JSON.stringify(v);
                return String(v);
              };
              return (
              <div key={field} className="flex items-start gap-2 text-xs flex-wrap">
                <span className="font-medium text-slate-500 min-w-[90px]">{formatFieldName(field)}:</span>
                <span className="text-red-500/80 line-through">{truncate(formatVal(diff.old), 40)}</span>
                <span className="text-slate-400">&rarr;</span>
                <span className="text-green-600">{truncate(formatVal(diff.new), 40)}</span>
              </div>
              );
            })}
          </div>
        )}

        {log.action === 'completed' && changes.notes && (
          <div className="mt-1.5 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1 inline-block">
            Notitie: {changes.notes}
          </div>
        )}

        {log.source && log.source !== 'manual' && (
          <Badge variant="outline" className="mt-1.5 text-[10px]">{log.source}</Badge>
        )}
      </div>
    </div>
  );
}
